import { Chart } from 'react-google-charts';
import '../../styles.scss';
import React, { useMemo, useCallback, useEffect, useRef } from 'react';
import type { GoogleChartWrapper } from 'react-google-charts/dist/types';
import type { DataSource } from '@features/report-output/common';
import { REPORT_COLORS, getChartImageData } from '../utils';
import type { PrioritizedAssessmentData } from '../../types';

// --- Constants for Chart Dimensions (same as BiDirectionalBarChart) ---
const CHART_WIDTH = 600;
const CHART_HEIGHT = 900;

const processChartData = (data: PrioritizedAssessmentData[]) => {
  return [
    ['Topic', 'Financial Score', { role: 'annotation' }, { role: 'style' }],
    ...data.map((topic) => {
      const score = topic.relativeScore ?? 0;
      return [
        topic.name,
        score,
        score ? `${score.toFixed(2)}%` : '',
        `color: ${REPORT_COLORS.FINANCIAL_COLUMN}; opacity: 1`,
      ];
    }),
  ];
};

const reportData: DataSource = {
  loaded: true,
  width: CHART_WIDTH,
  height: CHART_HEIGHT,
};

export interface SingleBarChartProps {
  data: PrioritizedAssessmentData[];
  updateData: (key: string, data: DataSource) => void;
}

export const SingleBarChart = React.memo(({ data, updateData }: SingleBarChartProps) => {
  const chartData = useMemo(() => processChartData(data), [data]);
  const hasCalledBack = useRef(false);

  // Reset callback flag when data changes
  useEffect(() => {
    hasCalledBack.current = false;
  }, [data]);

  const handleChartReady = useCallback(
    ({ chartWrapper }: { chartWrapper: GoogleChartWrapper }) => {
      if (hasCalledBack.current) return;
      hasCalledBack.current = true;

      const chartImageData = getChartImageData(chartWrapper);
      updateData('financialMaterialityChart', {
        ...reportData,
        chart: chartImageData,
      });
    },
    [updateData],
  );

  return (
    <div
      className='assessment__insights-chart'
      style={{
        padding: '20px 0',
        width: CHART_WIDTH,
        height: CHART_HEIGHT + 80,
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        overflow: 'visible',
      }}
    >
      <div className='d-flex text-ThemeHeadingMedium mb-2'>
        <div className='h4' style={{ textAlign: 'center' }}>
          Financial Materiality
        </div>
      </div>
      <Chart
        chartType='BarChart'
        width={CHART_WIDTH}
        height={CHART_HEIGHT}
        data={chartData}
        chartEvents={[
          {
            eventName: 'ready',
            callback: handleChartReady,
          },
        ]}
        options={{
          legend: 'none',
          bar: { groupWidth: '80%' },
          chartArea: {
            height: '100%',
            width: '60%',
            left: '35%',
          },
          hAxis: {
            textPosition: 'none',
          },
          vAxis: {
            textStyle: {
              fontSize: 10,
            },
            maxTextLines: 1, // Truncate long labels to single line with ellipsis
          },
          annotations: {
            textStyle: {
              fontSize: 12,
              fontName: 'Arial, sans-serif',
            },
            highContrast: true,
            alwaysOutside: data.length < 25,
            stem: {
              length: 0,
            },
          },
        }}
      />
    </div>
  );
});

SingleBarChart.displayName = 'SingleBarChart';

